import { setCookie, removeCookie } from 'typescript-cookie';

import { FirebaseApp, FirebaseOptions, FirebaseAppSettings, initializeApp } from "firebase/app";
import { Auth, User, connectAuthEmulator, getAuth, onAuthStateChanged, signOut } from "firebase/auth";

import { API } from './api';
import { Component, ComponentEventDataMap } from '@subgraph-io/edge/component';

export type SessionStandaloneEventMap = ComponentEventDataMap & {
    authStateChanged: User | null
}

export class SessionStandalone extends Component<SessionStandaloneEventMap> {

    protected firebaseApp: FirebaseApp;
    protected firebaseAuth: Auth;
    protected api: API;

    protected userNavigation: any[];
    protected loggedInUser: User | null;

    constructor(userNavigation: any[], firebaseOptions: FirebaseOptions, firebaseAppSettings?: FirebaseAppSettings) {
        super();
        this.userNavigation = userNavigation;
        this.firebaseApp = initializeApp(firebaseOptions, firebaseAppSettings);
        this.firebaseAuth = getAuth(this.firebaseApp);
        this.api = new API();
        this.loggedInUser = null;

        // firebase has a convention that a project id that starts with demo does not use
        // real firebase resources, which is what we want during local dev using the emulator
        if (firebaseOptions.projectId?.startsWith('demo')) {
            // the only emulator we need to activate for client code is the auth emulator
            connectAuthEmulator(this.firebaseAuth, "http://localhost:9099");
        }

        onAuthStateChanged(this.firebaseAuth, async (user: User | null) => {
            if (user !== null) {
                this.loggedInUser = user;
                this.setCookie();
                this.triggerEvent('CLASS_EVENT', 'authStateChanged', user);
            }
            else {
                this.loggedInUser = null;
                removeCookie('__session', {
                    domain: ".sixclear.com",
                    path: "/"
                });
                removeCookie('session_id', {
                    domain: ".sixclear.com",
                    path: "/"
                });
                this.triggerEvent('CLASS_EVENT', 'authStateChanged', null);
            }
        });
    }

    public async setCookie() {
        if (this.loggedInUser) {
            const token = await this.loggedInUser.getIdToken(true);

            // this cookie is used by sixclear.com
            setCookie('__session', token, {
                domain: ".sixclear.com",
                // chrome recently changed behavior and only allows cookies to be set to expire 400 days in the future
                // https://developer.chrome.com/blog/cookie-max-age-expires/
                expires: new Date(Date.now() + (400 * 86400000)),
                path: "/"
            });

            // this cookie is used by portal
            setCookie('session_id', this.loggedInUser.uid, {
                domain: ".sixclear.com",
                // chrome recently changed behavior and only allows cookies to be set to expire 400 days in the future
                // https://developer.chrome.com/blog/cookie-max-age-expires/
                expires: new Date(Date.now() + (400 * 86400000)),
                path: "/"
            });

        }
    }

    public getFirebaseApp() {
        return this.firebaseApp;
    }

    public getFirebaseAuth() {
        return this.firebaseAuth;
    }

    public getAPI() {
        return this.api;
    }

    public getLoggedInUser() {
        return this.loggedInUser;
    }

    public getUserNavigation() {
        return this.userNavigation;
    }

    public addErrors(errors: {message: string}[]) {

    }

    public clearErrors() {

    }

    public async signOut() {
        try {
            removeCookie('__session', {
                domain: ".sixclear.com",
                path: "/"
            });
            removeCookie('session_id', {
                domain: ".sixclear.com",
                path: "/"
            });
            await signOut(this.firebaseAuth);
            await this.sendPortalSignOut();
            window.location.href = '/views/auth/signin';
        } catch (error) {
            this.addErrors([{ message: 'An unknown error occurred and you could not be signed out. Please try again.' }])
        }
    }

    public async sendPortalSignOut() {
        // to sign out the portal session we need to hit an endpoint from the server,
        // so send api request to do it
        try {
            await this.api.signOut();
        }
        catch (error) {
            console.error(error);
        }
    }

}

export function init(userNavigation: ConstructorParameters<typeof SessionStandalone>[0], firebaseOptions: ConstructorParameters<typeof SessionStandalone>[1], firebaseAppSettings: ConstructorParameters<typeof SessionStandalone>[2]): SessionStandalone | void {
    try {
        return new SessionStandalone(userNavigation, firebaseOptions, firebaseAppSettings);
    } catch (error) {
        console.error(error);
    }
}